
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import MenuSection from './components/MenuSection';
import GallerySection from './components/GallerySection';
import ReservationSection from './components/ReservationSection';
import ContactSection from './components/ContactSection';
import FooterSection from './components/FooterSection';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import SakuraAnimation from './components/SakuraAnimation';

export default function HomePage() {
  useEffect(() => {
    // Add JSON-LD structured data for restaurant
    const restaurantSchema = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": "TAKEYA (竹や)",
      "description": "Restoran Jepang futuristik satu-satunya di Bukittinggi. Nikmati rasa autentik asli Jepang dengan teknologi modern dan pelayanan terdepan.",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "telephone": "+62 8127543743",
      "priceRange": "$$",
      "servesCuisine": "Japanese",
      "acceptsReservations": true,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Jl. Sakura No. 123",
        "addressLocality": "Bukittinggi",
        "addressRegion": "Sumatera Barat",
        "addressCountry": "ID"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -0.3037,
        "longitude": 100.3693
      },
      "openingHours": [
        "Mo-Su 10:00-22:00"
      ],
      "image": "https://public.readdy.ai/ai/img_res/e7d100120522c5b464710e118ebceddf.jpg",
      "hasMenu": import.meta.env.VITE_SITE_URL ? `${import.meta.env.VITE_SITE_URL}/customer` : "https://example.com/customer",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
      }
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "TAKEYA Restaurant",
      "alternateName": "TAKEYA (竹や)",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": "https://readdy.ai/api/search-image?query=Japanese%20restaurant%20logo%20with%20bamboo%20elements%2C%20minimalist%20design%2C%20text%20TAKEYA%2C%20professional%20branding&width=200&height=200&seq=takeya-logo&orientation=squarish",
      "sameAs": [
        "https://instagram.com/takeya.restaurant",
        "https://facebook.com/takeya.restaurant"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+62 8127543743",
        "contactType": "customer service",
        "availableLanguage": ["Indonesian", "Japanese"]
      }
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "TAKEYA Restaurant",
      "alternateName": "TAKEYA (竹や)",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "description": "Restoran Jepang futuristik satu-satunya di Bukittinggi. Nikmati rasa autentik asli Jepang dengan teknologi modern dan pelayanan terdepan.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/customer?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());

    // Add new schema scripts
    const restaurantScript = document.createElement('script');
    restaurantScript.type = 'application/ld+json';
    restaurantScript.textContent = JSON.stringify(restaurantSchema);
    document.head.appendChild(restaurantScript);

    const organizationScript = document.createElement('script');
    organizationScript.type = 'application/ld+json';
    organizationScript.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(organizationScript);

    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.textContent = JSON.stringify(websiteSchema);
    document.head.appendChild(websiteScript);

    // Update page title and meta description
    document.title = "TAKEYA (竹や) - Restoran Jepang Futuristik di Bukittinggi";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'TAKEYA (竹や) - Restoran Jepang futuristik satu-satunya di Bukittinggi. Nikmati rasa autentik asli Jepang dengan teknologi modern dan pelayanan terdepan.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'biaro, jepang, restoran jepang, bukittinggi, ramen, sushi, takeya, futuristik, kuliner jepang, sumatera barat');
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'TAKEYA (竹や) - Restoran Jepang Futuristik di Bukittinggi');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'TAKEYA (竹や) - Restoran Jepang futuristik satu-satunya di Bukittinggi. Nikmati rasa autentik asli Jepang dengan teknologi modern dan pelayanan terdepan.');
    }

    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'TAKEYA (竹や) - Restoran Jepang Futuristik di Bukittinggi');
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'TAKEYA (竹や) - Restoran Jepang futuristik satu-satunya di Bukittinggi. Nikmati rasa autentik asli Jepang dengan teknologi modern dan pelayanan terdepan.');
    }

    // Cleanup function to remove schemas when component unmounts
    return () => {
      const schemas = document.querySelectorAll('script[type="application/ld+json"]');
      schemas.forEach(script => script.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <SakuraAnimation />
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <MenuSection />
        <GallerySection />
        <ReservationSection />
        <ContactSection />
      </main>
      <FooterSection />
      <WhatsAppFloat />
    </div>
  );
}
